import React from 'react';
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import cn from '../utils/cn';

const SizeContext = React.createContext<'sm' | 'md'>('md');

type KoobSegmentedControlProps = Omit<
  ToggleGroupPrimitive.ToggleGroupSingleProps,
  'type' | 'className'
> & {
  /** sm lines up with sm buttons; md lines up with text fields */
  size?: 'sm' | 'md';
  className?: string;
};

const KoobSegmentedControl = React.forwardRef<
  HTMLDivElement,
  KoobSegmentedControlProps
>(
  (
    { value, onValueChange, children, size = 'md', className, ...props },
    ref,
  ) => (
    <SizeContext.Provider value={size}>
      <ToggleGroupPrimitive.Root
        ref={ref}
        type="single"
        value={value}
        // ponytail: swallow Radix's deselect ('' value) — a segmented control always keeps one selected
        onValueChange={(v) => v && onValueChange?.(v)}
        // Heights match controlHeights so it lines up with buttons/text fields in
        // filter rows; no self-alignment override so parent rows (items-end
        // toolbars) control vertical placement — the fixed height prevents
        // stretching either way.
        // max-w-full + min-w-0 so a control with long labels shrinks with its
        // container (and can shrink as a flex child) instead of overflowing it;
        // the items handle the shrinking from there.
        className={cn(
          'inline-flex min-w-0 max-w-full items-center rounded-lg border border-gray-200 bg-gray-100/50 p-0.5',
          // Same disabled treatment as KoobInputText.
          'data-[disabled]:cursor-not-allowed data-[disabled]:bg-gray-100 data-[disabled]:opacity-50',
          size === 'sm' ? 'h-8 rounded-md' : 'h-10',
          className,
        )}
        {...props}
        // Radix only marks the items; the root needs it for the styles above.
        data-disabled={props.disabled ? '' : undefined}
      >
        {children}
      </ToggleGroupPrimitive.Root>
    </SizeContext.Provider>
  ),
);
KoobSegmentedControl.displayName = 'KoobSegmentedControl';

type KoobSegmentedControlItemProps = Omit<
  ToggleGroupPrimitive.ToggleGroupItemProps,
  'className'
> & {
  className?: string;
};

const KoobSegmentedControlItem = React.forwardRef<
  HTMLButtonElement,
  KoobSegmentedControlItemProps
>((props, ref) => {
  const size = React.useContext(SizeContext);
  const { className, ...itemProps } = props;
  return (
    <ToggleGroupPrimitive.Item
      ref={ref}
      className={cn(
        'inline-flex h-full min-w-0 items-center justify-center overflow-hidden text-ellipsis whitespace-nowrap rounded-md border-none bg-transparent transition-all focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-k-gd2 text-gray-500 hover:text-gray-600 data-[state=on]:bg-white data-[state=on]:shadow-sm data-[state=on]:text-gray-900',
        'disabled:cursor-not-allowed disabled:hover:text-gray-500 data-[state=on]:disabled:shadow-none',
        // text/padding match KoobButton of the same size
        size === 'sm' ? 'px-3 text-xs' : 'px-2.5 text-sm',
        className,
      )}
      {...itemProps}
    />
  );
});
KoobSegmentedControlItem.displayName = 'KoobSegmentedControlItem';

export { KoobSegmentedControl, KoobSegmentedControlItem };
